import { createBrowserRouter, Outlet } from "react-router";
import { RouterProvider } from "react-router/dom";
import { SWRConfig } from "swr";
import "material-icons/iconfont/material-icons.css";
import "./assets/styles/index.css";
import Register from "pages/user/register";
import Login from "./pages/user/login";
import InviteAccept from "./pages/user/invite-accept";
import AdminWrapper from "./admin-wrapper";
import BuildSurvey from "./pages/survey/build";
import ListSurvey from "./pages/survey/list";
import LandingSurvey from "./pages/survey/landing";
import RespondSurvey from "./pages/survey/respond";
import ResetPassword from "./pages/user/resetpassword";
import ResetConfirm from "./pages/user/resetconfirm";
import Dashboard from "./pages/dashboard";
import Account from "./pages/user/account";
import Organisation from "./pages/user/organisation";
import Payment from "pages/user/payment";
import TopUps from "pages/user/top-ups";
import Logout from "./pages/user/logout";
import { ToastContainer, Flip } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchSession } from "./store/auth";
import { AUTH_STATUS } from "./store/auth-status";
import { LoadingProvider } from "./contexts/loading-context";
import LoadingBar from "./components/ui/loading-bar";
import { isApiError } from "utils/api-error";

// Route config lives at module scope. Elements are React element
// descriptors -- they don't render until the router mounts them
// inside AppLayout, which provides Redux/SWR context.
const router = createBrowserRouter([
    {
        element: <AppLayout />,
        children: [
            { path: "/invite/:token", element: <InviteAccept /> },
            { path: "/login", element: <Login /> },
            { path: "/resetpassword", element: <ResetPassword /> },
            { path: "/resetconfirm", element: <ResetConfirm /> },
            { path: "/logout", element: <Logout /> },
            { path: "/register", element: <Register /> },
            {
                path: "/respond/:surveyNanoid",
                element: <LandingSurvey />,
            },
            {
                path: "/respond/:surveyNanoid/answer",
                element: <RespondSurvey />,
            },
            {
                path: "/",
                element: <AdminWrapper />,
                children: [
                    { index: true, element: <Dashboard /> },
                    { path: "account", element: <Account /> },
                    { path: "organisation", element: <Organisation /> },
                    { path: "payment", element: <Payment /> },
                    { path: "top-ups", element: <TopUps /> },
                    {
                        path: "survey",
                        children: [
                            { index: true, element: <ListSurvey /> },
                            { path: ":surveyId", element: <BuildSurvey /> },
                        ],
                    },
                ],
            },
        ],
    },
]);

function AppLayout() {
    const dispatch = useDispatch();
    const auth = useSelector((state) => state.auth);
    useEffect(() => {
        if (auth.status === AUTH_STATUS.UNKNOWN) {
            dispatch(fetchSession());
        }
    }, [dispatch, auth.status]);

    return (
        <LoadingProvider>
            <LoadingBar />
            <SWRConfig
                value={{
                    onError: (error) => {
                        if (isApiError(error) && error.status === 403) {
                            return;
                        }
                    },
                    shouldRetryOnError: (error) => {
                        if (isApiError(error) && error.status === 403) return false;
                        if (isApiError(error) && !error.isRetryable) return false;
                        return true;
                    },
                    revalidateOnFocus: false,
                }}
            >
                <ToastContainer
                    position="bottom-center"
                    autoClose={4000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    draggable
                    transition={Flip}
                    pauseOnHover
                    theme="light"
                />
                <Outlet />
            </SWRConfig>
        </LoadingProvider>
    );
}

export default function App() {
    return <RouterProvider router={router} />;
}
